import React, { useEffect } from 'react';

import useServerSideRendering from '@hooks/useServerSideRendering';
import { GoogleOAuthProvider, MomentListener, useGoogleOneTapLogin } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID, GOOGLE_ONE_TAP_DEBUG } from '@src/env';
import { GOOGLE_PROVIDER, createLeadWithOauth } from '@utils/backend-api/oauth';

interface GoogleOneTapProps {
  isGdprRequired: boolean;
  newsletterIds: number[];
  redirectBasePath: string;
  returnTo: string;
}

const DISABLED_PATHS = ['/login', '/subscribe'];

// Keep in sync with OneTap::FORM_LOCATION and OneTap::LEAD_TYPE in app/models/concerns/one_tap.rb
const FORM_LOCATION = 'one_tap_sitewide';
const LEAD_TYPE = 'one_tap_lead';

// console.debug is not shimmed in the SSR bundle (mini_racer), so use log/error.
const debugLog = (...args: unknown[]) => {
  if (GOOGLE_ONE_TAP_DEBUG) console.log('[OneTap]', ...args);
};
const debugError = (...args: unknown[]) => {
  if (GOOGLE_ONE_TAP_DEBUG) console.error('[OneTap]', ...args);
};

// Only passed to GSI when debugging: these status methods are deprecated under FedCM
// and GSI logs a warning whenever a listener is registered.
const logPromptMoment: MomentListener = (notification) => {
  debugLog('promptMomentNotification:', {
    isDisplayMoment: notification.isDisplayMoment(),
    isDisplayed: notification.isDisplayed(),
    isNotDisplayed: notification.isNotDisplayed(),
    getNotDisplayedReason: notification.isNotDisplayed()
      ? notification.getNotDisplayedReason()
      : null,
    isSkippedMoment: notification.isSkippedMoment(),
    getSkippedReason: notification.isSkippedMoment() ? notification.getSkippedReason() : null,
    isDismissedMoment: notification.isDismissedMoment(),
    getDismissedReason: notification.isDismissedMoment() ? notification.getDismissedReason() : null,
  });
};

function GoogleOneTapInner({
  isGdprRequired,
  newsletterIds,
  redirectBasePath,
  returnTo,
}: GoogleOneTapProps) {
  const isDisabled = DISABLED_PATHS.includes(window.location.pathname);

  debugLog('GoogleOneTapInner rendered. props:', {
    isGdprRequired,
    newsletterIds,
    redirectBasePath,
    returnTo,
  });
  debugLog('Current path:', window.location.pathname, '| isDisabled:', isDisabled);

  useEffect(() => {
    debugLog('GoogleOneTapInner mounted.');
    debugLog('window.google present:', !!(window as any).google);
    debugLog('window.google.accounts present:', !!(window as any).google?.accounts);
    debugLog('google.accounts.id present:', !!(window as any).google?.accounts?.id);
  }, []);

  useGoogleOneTapLogin({
    disabled: isDisabled,
    use_fedcm_for_prompt: true,
    cancel_on_tap_outside: true,
    auto_select: false,
    promptMomentNotification: GOOGLE_ONE_TAP_DEBUG ? logPromptMoment : undefined,
    onSuccess: async (credentialResponse) => {
      debugLog('onSuccess fired. credential present:', !!credentialResponse.credential);
      try {
        const payload = {
          id_token: credentialResponse.credential,
          provider: GOOGLE_PROVIDER,
          form_location: FORM_LOCATION,
          lead_type: LEAD_TYPE,
          newsletter_ids: newsletterIds,
          opt_in_required: isGdprRequired,
          declined_opt_in: isGdprRequired,
          page: window.location.pathname,
          redirect_to_on_success: `${redirectBasePath}?return_to=${encodeURIComponent(returnTo)}`,
        };
        debugLog('createLeadWithOauth payload:', payload);
        const response = await createLeadWithOauth(payload);
        debugLog('createLeadWithOauth response:', response);
        const separator = response.location.includes('?') ? '&' : '?';
        window.location.href = `${response.location}${separator}new=${response.isNewUser}`;
      } catch (error) {
        // Silent fail unless debugging — One Tap is ambient, errors should not interrupt reading
        debugError('createLeadWithOauth failed:', error);
      }
    },
    onError: () => {
      debugError('useGoogleOneTapLogin onError fired');
    },
  });

  return null;
}

export default function GoogleOneTap(props: GoogleOneTapProps) {
  const hydrated = useServerSideRendering();

  debugLog(
    'GoogleOneTap outer rendered. hydrated:',
    hydrated,
    '| GOOGLE_CLIENT_ID:',
    GOOGLE_CLIENT_ID ? `set (${GOOGLE_CLIENT_ID.slice(0, 12)}...)` : 'MISSING/EMPTY'
  );

  if (!hydrated) return null;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <GoogleOneTapInner {...props} />
    </GoogleOAuthProvider>
  );
}
